import React, { useEffect, useState, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';

const slideIn = keyframes`
  from {
    transform: translateX(400px);
    opacity: 0;
    max-height: 0;
    margin-bottom: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
    max-height: 200px;
    margin-bottom: 0;
  }
`;

const slideOut = keyframes`
  0% {
    transform: translateX(0);
    opacity: 1;
    max-height: 200px;
  }
  60% {
    transform: translateX(400px);
    opacity: 0;
    max-height: 200px;
  }
  100% {
    transform: translateX(400px);
    opacity: 0;
    max-height: 0;
    padding-top: 0;
    padding-bottom: 0;
    margin: 0;
  }
`;

const bounce = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
`;

const NotificationContainer = styled.div`
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 0;
  pointer-events: none;

  @media (max-width: 768px) {
    top: 16px;
    right: 16px;
    left: 16px;
  }
`;

const NotificationCard = styled.div`
  background: ${p => {
    switch (p.$type) {
      case 'success': return 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
      case 'error': return 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
      case 'warning': return 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
      default: return 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
    }
  }};
  color: white;
  padding: 16px 20px;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  pointer-events: auto;
  width: 380px;
  max-width: calc(100vw - 48px);
  position: relative;
  overflow: hidden;
  margin-bottom: 12px;
  animation: ${p => p.$isClosing ? slideOut : slideIn} ${p => p.$isClosing ? '0.4s' : '0.3s'} ease-out forwards;

  @media (max-width: 768px) {
    width: 100%;
    max-width: none;
    padding: 14px 16px;
  }
`;

const NotificationContent = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const IconWrapper = styled.div`
  font-size: 24px;
  flex-shrink: 0;
  animation: ${bounce} 0.5s ease-out;
  line-height: 1;
`;

const TextContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotificationTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  margin-bottom: 4px;
  line-height: 1.4;
`;

const NotificationMessage = styled.div`
  font-size: 13px;
  opacity: 0.95;
  line-height: 1.5;
  font-weight: 400;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
  flex-shrink: 0;
  opacity: 0.8;
  &:hover { opacity: 1; background: rgba(255, 255, 255, 0.2); }
`;

const ProgressBarTrack = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: rgba(255, 255, 255, 0.15);
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background: rgba(255, 255, 255, 0.4);
  transition: width 0.1s linear;
`;

const getIcon = (type) => {
  switch (type) {
    case 'success': return '✅';
    case 'error': return '❌';
    case 'warning': return '⚠️';
    default: return 'ℹ️';
  }
};

const Notification = ({ type = 'info', title, message, duration = 5000, onClose, id }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [progressWidth, setProgressWidth] = useState(100);
  const isPausedRef = useRef(false);
  const closedRef = useRef(false);
  const startTimeRef = useRef(Date.now());
  const remainingRef = useRef(duration);
  const rafRef = useRef(null);

  const doClose = useCallback(() => {
    if (closedRef.current) return;
    closedRef.current = true;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    setIsClosing(true);
    // Don't call onClose here — wait for animationend
  }, []);

  // Remove from parent when the close animation finishes
  const handleAnimationEnd = useCallback(() => {
    if (closedRef.current) {
      onClose && onClose(id);
    }
  }, [id, onClose]);

  // Progress bar + auto-close via RAF
  useEffect(() => {
    if (!duration || duration <= 0) return;

    const tick = () => {
      if (closedRef.current) return;
      if (!isPausedRef.current) {
        const elapsed = Date.now() - startTimeRef.current;
        const remaining = remainingRef.current - elapsed;
        const pct = Math.max(0, (remaining / duration) * 100);
        setProgressWidth(pct);
        if (remaining <= 0) {
          doClose();
          return;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    startTimeRef.current = Date.now();
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [duration, doClose]);

  // Safety fallback — if animationend somehow doesn't fire
  useEffect(() => {
    if (!isClosing) return;
    const fallback = setTimeout(() => { onClose && onClose(id); }, 600);
    return () => clearTimeout(fallback);
  }, [isClosing, id, onClose]);

  const handleMouseEnter = useCallback(() => {
    isPausedRef.current = true;
    const elapsed = Date.now() - startTimeRef.current;
    remainingRef.current = Math.max(0, remainingRef.current - elapsed);
  }, []);

  const handleMouseLeave = useCallback(() => {
    isPausedRef.current = false;
    startTimeRef.current = Date.now();
  }, []);

  return (
    <NotificationCard
      $type={type}
      $isClosing={isClosing}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onAnimationEnd={handleAnimationEnd}
    >
      <NotificationContent>
        <IconWrapper>{getIcon(type)}</IconWrapper>
        <TextContent>
          <NotificationTitle>{title}</NotificationTitle>
          {message && <NotificationMessage>{message}</NotificationMessage>}
        </TextContent>
        <CloseButton onClick={doClose}>×</CloseButton>
      </NotificationContent>
      {duration && duration > 0 && !isClosing && (
        <ProgressBarTrack>
          <ProgressBarFill style={{ width: `${progressWidth}%` }} />
        </ProgressBarTrack>
      )}
    </NotificationCard>
  );
};

const NotificationProvider = ({ notifications, onRemove }) => (
  <NotificationContainer>
    {notifications.map((notification) => (
      <Notification key={notification.id} {...notification} onClose={onRemove} />
    ))}
  </NotificationContainer>
);

export default NotificationProvider;
export { Notification };
